import useUploadImageService from 'api/services/uploadImageService'
import { resolveUploadImageUrl } from 'api/utils/resolveUploadImageUrl'
import classNames from 'classnames'
import Loading from 'components/common/loading/Loading'
import { useLoading } from 'components/common/loading/useLoading'
import { ChangeEvent, DragEvent, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  appendListingPhotos,
  createListingPhotoId,
  ListingPhoto,
  removeListingPhoto,
  reorderPhotosByIds,
} from './listingPhotos'

export const MAX_LISTING_PHOTOS = 10
const MAX_FILE_BYTES = 5 * 1024 * 1024
/** Keep each request small so Hostinger uploads stay under the request timeout. */
const UPLOAD_BATCH_SIZE = 3

type CreateAdPhotoUploadProps = {
  photos: ListingPhoto[]
  onPhotosChange: (photos: ListingPhoto[]) => void
  virtualTourLink: string
  onVirtualTourLinkChange: (value: string) => void
  photosRightsConfirmed: boolean
  onPhotosRightsConfirmedChange: (value: boolean) => void
}

const IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']

function isValidImageFile(file: File): boolean {
  if (IMAGE_TYPES.includes(file.type)) return true
  const name = file.name.toLowerCase()
  return (
    name.endsWith('.jpg') ||
    name.endsWith('.jpeg') ||
    name.endsWith('.png') ||
    name.endsWith('.webp')
  )
}

function CameraUploadIcon() {
  return (
    <div className="relative inline-flex text-imovPrimary">
      <svg className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039H9.352a2.192 2.192 0 00-1.736 1.039l-.822 1.316z"
        />
        <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0z" />
      </svg>
      <span className="absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-imovPrimary text-[10px] font-bold text-white">
        +
      </span>
    </div>
  )
}

function StarIcon() {
  return (
    <svg className="h-3 w-3 shrink-0" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 2l2.9 6.26L22 9.27l-5 4.87L18.18 22 12 18.27 5.82 22 7 14.14 2 9.27l7.1-1.01L12 2z" />
    </svg>
  )
}

function LinkIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
      />
    </svg>
  )
}

type PhotoThumbnailProps = {
  photo: ListingPhoto
  isCover: boolean
  isLoading: boolean
  isDragging: boolean
  isDragOver: boolean
  onRemove: () => void
  onDragStart: () => void
  onDragEnd: () => void
  onDragOver: (event: DragEvent<HTMLLIElement>) => void
  onDrop: () => void
}

const PhotoThumbnail = ({
  photo,
  isCover,
  isLoading,
  isDragging,
  isDragOver,
  onRemove,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
}: PhotoThumbnailProps) => {
  const { t } = useTranslation()

  return (
    <li
      draggable={!isLoading}
      onDragStart={event => {
        event.dataTransfer.effectAllowed = 'move'
        event.dataTransfer.setData('text/plain', photo.id)
        onDragStart()
      }}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      onDrop={event => {
        event.preventDefault()
        onDrop()
      }}
      className={classNames(
        'group relative aspect-[4/3] cursor-grab overflow-hidden rounded-xl bg-gray-100 active:cursor-grabbing',
        isCover ? 'ring-2 ring-imovPrimary ring-offset-2' : 'ring-1 ring-gray-200',
        isDragOver && 'ring-2 ring-imovPrimary/60',
        isDragging && 'opacity-50'
      )}
    >
      <img
        src={resolveUploadImageUrl(photo.url)}
        alt=""
        className="h-full w-full object-cover"
        loading="lazy"
        draggable={false}
      />
      {isCover ? (
        <span className="absolute left-2 top-2 z-10 inline-flex max-w-[calc(100%-3rem)] items-center gap-1 rounded-lg bg-imovPrimary px-2 py-1 text-[11px] font-semibold leading-tight text-white shadow-md">
          <StarIcon />
          <span className="truncate">{t('createAd.flow.photos.mainPhotoBadge')}</span>
        </span>
      ) : null}
      <button
        type="button"
        disabled={isLoading}
        onClick={onRemove}
        className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-black/55 text-white opacity-0 transition-opacity hover:bg-black/70 group-hover:opacity-100 focus:opacity-100 disabled:opacity-40"
        aria-label={t('createAd.flow.photos.removePhoto')}
      >
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </li>
  )
}

const CreateAdPhotoUpload = ({
  photos,
  onPhotosChange,
  virtualTourLink,
  onVirtualTourLinkChange,
  photosRightsConfirmed,
  onPhotosRightsConfirmedChange,
}: CreateAdPhotoUploadProps) => {
  const { t } = useTranslation()
  const { isLoading, setLoading } = useLoading()
  const { uploadImage } = useUploadImageService()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [draggedId, setDraggedId] = useState<string | null>(null)
  const [dragOverId, setDragOverId] = useState<string | null>(null)
  const [isUploadDragOver, setIsUploadDragOver] = useState(false)

  const slotsRemaining = MAX_LISTING_PHOTOS - photos.length
  const canAddMore = slotsRemaining > 0

  const uploadFiles = async (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0 || !canAddMore) return

    const allFiles = Array.from(fileList)
    const tooLarge = allFiles.filter(file => file.size > MAX_FILE_BYTES)
    if (tooLarge.length > 0) {
      window.alert(t('createAd.flow.photos.fileTooLarge'))
    }

    const files = allFiles
      .filter(file => file.size <= MAX_FILE_BYTES)
      .filter(isValidImageFile)
      .slice(0, slotsRemaining)

    if (files.length !== allFiles.length && tooLarge.length === 0) {
      window.alert(t('createAd.flow.photos.invalidType'))
    }
    if (files.length === 0) return

    if (allFiles.length > slotsRemaining) {
      window.alert(t('createAd.flow.photos.maxPhotos', { max: MAX_LISTING_PHOTOS }))
    }

    setLoading(true)
    try {
      const collectedUrls: string[] = []
      for (let i = 0; i < files.length; i += UPLOAD_BATCH_SIZE) {
        const batch = files.slice(i, i + UPLOAD_BATCH_SIZE)
        const formData = new FormData()
        batch.forEach(file => formData.append('images[]', file))
        const response = await uploadImage(formData)
        const urls = Array.isArray(response?.urls) ? response.urls : []
        for (const url of urls) {
          if (typeof url === 'string' && url.trim() !== '') {
            collectedUrls.push(url)
          }
        }
      }

      if (collectedUrls.length === 0) {
        window.alert(t('createAd.flow.photos.uploadFailed'))
        return
      }

      const additions: ListingPhoto[] = collectedUrls
        .map(
          (url): ListingPhoto => ({
            id: createListingPhotoId(),
            url: resolveUploadImageUrl(url),
            category: '',
          })
        )
        .filter(photo => photo.url !== '')

      onPhotosChange(appendListingPhotos(photos, additions))
    } catch {
      window.alert(t('createAd.flow.photos.uploadFailed'))
    } finally {
      setLoading(false)
    }
  }

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    void uploadFiles(event.target.files)
    event.target.value = ''
  }

  const handleUploadDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setIsUploadDragOver(false)
    void uploadFiles(event.dataTransfer.files)
  }

  const clearDragState = () => {
    setDraggedId(null)
    setDragOverId(null)
  }

  const handlePhotoDrop = (targetId: string) => {
    if (!draggedId || draggedId === targetId) {
      clearDragState()
      return
    }
    onPhotosChange(reorderPhotosByIds(photos, draggedId, targetId))
    clearDragState()
  }

  const openFilePicker = () => {
    if (!isLoading && canAddMore) {
      fileInputRef.current?.click()
    }
  }

  return (
    <div className="create-ad-photo-upload relative space-y-5 rounded-2xl border border-gray-100 bg-white p-5 shadow-sm sm:p-6">
      {isLoading && (
        <div className="absolute inset-0 z-20 flex items-center justify-center rounded-2xl bg-white/80">
          <Loading />
          <span className="sr-only">{t('createAd.flow.photos.uploading')}</span>
        </div>
      )}

      <div
        onDragOver={event => {
          if (!canAddMore) return
          event.preventDefault()
          setIsUploadDragOver(true)
        }}
        onDragLeave={() => setIsUploadDragOver(false)}
        onDrop={handleUploadDrop}
        className={classNames(
          'flex flex-col items-center justify-center rounded-2xl border-2 border-dashed px-6 py-10 text-center transition-colors',
          canAddMore
            ? isUploadDragOver
              ? 'border-imovPrimary bg-imovPrimary/5'
              : 'border-gray-200 bg-gray-50/50 hover:border-imovPrimary/40'
            : 'border-gray-100 bg-gray-50 opacity-60'
        )}
      >
        <CameraUploadIcon />
        <p className="mt-4 text-base font-semibold text-gray-900">{t('createAd.flow.photos.dropTitle')}</p>
        <button
          type="button"
          disabled={!canAddMore || isLoading}
          onClick={openFilePicker}
          className="mt-2 text-sm font-medium text-imovPrimary underline underline-offset-2 hover:text-imovPrimaryDark disabled:cursor-not-allowed disabled:no-underline disabled:opacity-50"
        >
          {t('createAd.flow.photos.browseFiles')}
        </button>
        <p className="mt-4 text-[10px] font-medium uppercase tracking-wider text-gray-400">
          {t('createAd.flow.photos.constraints')}
        </p>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/webp"
          multiple
          className="sr-only"
          disabled={isLoading || !canAddMore}
          onChange={handleFileChange}
        />
      </div>

      <div className="rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-xs leading-relaxed text-gray-700">
        <span className="font-semibold text-imovPrimary">{t('createAd.flow.photos.tipLabel')}</span>{' '}
        {t('createAd.flow.photos.tipText')}
      </div>

      {photos.length > 0 ? (
        <>
          <ul className="grid grid-cols-3 gap-3">
            {photos.map((photo, index) => (
              <PhotoThumbnail
                key={photo.id}
                photo={photo}
                isCover={index === 0}
                isLoading={isLoading}
                isDragging={draggedId === photo.id}
                isDragOver={dragOverId === photo.id && draggedId !== photo.id}
                onRemove={() => onPhotosChange(removeListingPhoto(photos, photo.id))}
                onDragStart={() => setDraggedId(photo.id)}
                onDragEnd={clearDragState}
                onDragOver={event => {
                  event.preventDefault()
                  event.dataTransfer.dropEffect = 'move'
                  setDragOverId(photo.id)
                }}
                onDrop={() => handlePhotoDrop(photo.id)}
              />
            ))}
          </ul>
          <p className="text-center text-xs italic text-gray-500">{t('createAd.flow.photos.reorderHint')}</p>
        </>
      ) : null}

      <div>
        <label
          className="mb-1.5 flex items-center gap-2 text-sm font-semibold text-gray-800"
          htmlFor="create-ad-virtual-tour"
        >
          <span className="text-imovPrimary">
            <LinkIcon />
          </span>
          {t('createAd.flow.photos.virtualTourLabel')}
        </label>
        <input
          id="create-ad-virtual-tour"
          type="url"
          value={virtualTourLink}
          onChange={e => onVirtualTourLinkChange(e.target.value)}
          placeholder={t('createAd.flow.photos.virtualTourPlaceholder')}
          className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm text-gray-900 focus:outline-none focus:ring-0"
        />
        <p className="mt-1.5 text-xs text-gray-500">{t('createAd.flow.photos.virtualTourHint')}</p>
      </div>

      <label className="flex cursor-pointer gap-3 rounded-xl bg-gray-50 p-4">
        <input
          type="checkbox"
          checked={photosRightsConfirmed}
          onChange={e => onPhotosRightsConfirmedChange(e.target.checked)}
          className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-imovPrimary focus:ring-imovPrimary"
        />
        <span className="text-xs leading-relaxed text-gray-600">{t('createAd.flow.photos.rightsConfirmation')}</span>
      </label>
    </div>
  )
}

export default CreateAdPhotoUpload
